import { normalizePostalCodeValue } from './validators';

const joinAddressParts = (parts = []) => (
  parts.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim()
);

const MANAGED_ADDRESS_FIELDS = new Set([
  'address1',
  'address2',
  'city',
  'district',
  'region',
  'postalCode'
]);

const getMappedFieldValue = (source, context) => {
  if (!source) {
    return '';
  }

  return String(
    source.split('.').reduce((value, key) => (
      value && value[key] !== undefined ? value[key] : undefined
    ), context) || ''
  ).trim();
};

const hasConfiguredMappings = (mappingType, addressElements = []) => (
  addressElements.some((element) => element.apiMappings?.[mappingType])
);

const buildMappedFieldsFromConfig = (mappingType, context, addressElements = []) => {
  const mapped = {};

  addressElements.forEach((element) => {
    if (!element?.field || !MANAGED_ADDRESS_FIELDS.has(element.field)) {
      return;
    }

    const value = getMappedFieldValue(element.apiMappings?.[mappingType], context);
    mapped[element.field] = element.field === 'postalCode' ? normalizePostalCodeValue(value) : value;
  });

  return mapped;
};

const STRUCTURED_LOCATION_TOKENS = new Set([
  'locality',
  'dependent_locality',
  'double_dependent_locality',
  'administrative_area',
  'sub_administrative_area',
  'postal_code',
  'sorting_code',
  'country'
]);

const stripEdgeCommasAndSpaces = (value = '') => {
  let start = 0;
  let end = value.length;

  while (start < end && [',', ' '].includes(value[start])) {
    start += 1;
  }

  while (end > start && [',', ' '].includes(value[end - 1])) {
    end -= 1;
  }

  return value.slice(start, end);
};

const normalizeFormattedLine = (value = '') => (
  stripEdgeCommasAndSpaces(
    String(value)
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .join(', ')
      .split(/\s+/)
      .filter(Boolean)
      .join(' ')
  )
);

const buildFormattedAddressLines = (addressFormat = '', components = {}) => (
  String(addressFormat)
    .split('|')
    .map((line) => normalizeFormattedLine(
      line.replace(/\b[a-z_]+\b/g, (token) => components[token] || '')
    ))
    .filter(Boolean)
);

const getAddressFormatTokens = (addressFormatLine = '') => (
  String(addressFormatLine).match(/[a-z_]+/g) || []
);

const isStructuredOnlyAddressFormatLine = (addressFormatLine = '') => {
  const tokens = getAddressFormatTokens(addressFormatLine);
  return tokens.length > 0 && tokens.every((token) => STRUCTURED_LOCATION_TOKENS.has(token));
};

export const buildUSFormattedAddress = (validatedAddress = {}) => {
  const components = validatedAddress.components || {};

  const streetLine = joinAddressParts([
    components.primary_number,
    components.street_predirection,
    components.street_name,
    components.street_suffix,
    components.street_postdirection
  ]);

  const secondaryLine = [
    joinAddressParts([components.secondary_designator, components.secondary_number]),
    joinAddressParts([components.extra_secondary_designator, components.extra_secondary_number]),
    joinAddressParts([components.pmb_designator, components.pmb_number])
  ].filter(Boolean).join(', ').trim();

  return {
    formatted: {
      address1: streetLine || validatedAddress.delivery_line_1 || '',
      address2: secondaryLine || validatedAddress.delivery_line_2 || ''
    }
  };
};

export const buildIntlFormattedAddress = (validatedAddress = {}) => {
  const components = validatedAddress.components || {};
  const addressFormat = validatedAddress.metadata?.address_format || validatedAddress.meta?.address_format || '';
  const addressFormatLines = String(addressFormat).split('|');
  const formattedAddressLines = buildFormattedAddressLines(addressFormat, components);
  const responseAddressLines = [
    validatedAddress.address1,
    validatedAddress.address2,
    validatedAddress.address3
  ].filter(Boolean);

  const fallbackAddress1 = joinAddressParts([components.thoroughfare, components.premise]);
  const leadingStreetLineIndexes = addressFormatLines.reduce((indexes, line, index) => {
    if (indexes.stopped) {
      return indexes;
    }

    if (isStructuredOnlyAddressFormatLine(line)) {
      indexes.stopped = true;
      return indexes;
    }

    if (formattedAddressLines[index]) {
      indexes.values.push(index);
    }

    return indexes;
  }, { values: [], stopped: false }).values;

  const address1 = (
    formattedAddressLines[leadingStreetLineIndexes[0]] ||
    fallbackAddress1 ||
    ''
  );
  const address2 = leadingStreetLineIndexes.length > 1 ?
    (formattedAddressLines[leadingStreetLineIndexes[1]] || '') :
    '';

  return {
    formatted: {
      address1,
      address2
    },
    responseAddressLines,
    formattedAddressLines
  };
};

export const buildIntlVerifiedAddressMapping = (validatedAddress = {}) => {
  const components = validatedAddress.components || {};
  const {
    formatted,
    responseAddressLines,
    formattedAddressLines
  } = buildIntlFormattedAddress(validatedAddress);

  return {
    formatted,
    mapped: {
      address1: formatted.address1,
      address2: formatted.address2,
      city: components.locality || '',
      postalCode: normalizePostalCodeValue(components.postal_code || '')
    },
    mapAddressLines: responseAddressLines.length ? responseAddressLines : formattedAddressLines
  };
};

export const buildIntlAutocompleteAddressMapping = (
  selectedAddress = {},
  addressForm = null
) => {
  const addressElements = addressForm?.addressElements || [];
  const fallbackMapped = {
    address1: selectedAddress.street || '',
    address2: '',
    city: selectedAddress.locality || '',
    region: selectedAddress.administrative_area || '',
    postalCode: normalizePostalCodeValue(selectedAddress.postal_code || '')
  };

  if (!hasConfiguredMappings('autocomplete', addressElements)) {
    return fallbackMapped;
  }

  return buildMappedFieldsFromConfig('autocomplete', selectedAddress, addressElements);
};

export const buildIntlConfiguredVerificationMapping = (
  validatedAddress = {},
  addressForm = null
) => {
  const fallback = buildIntlVerifiedAddressMapping(validatedAddress);
  const addressElements = addressForm?.addressElements || [];

  if (!hasConfiguredMappings('verification', addressElements)) {
    return fallback;
  }

  const mappingContext = {
    ...validatedAddress,
    formatted: fallback.formatted
  };

  return {
    ...fallback,
    mapped: buildMappedFieldsFromConfig('verification', mappingContext, addressElements)
  };
};

export const preserveBlankConfiguredMappings = (mapped = {}, currentValues = {}, addressElements = [], mappingType) => {
  const nextMapped = { ...mapped };

  addressElements.forEach((element) => {
    if (!element?.field || !MANAGED_ADDRESS_FIELDS.has(element.field)) {
      return;
    }

    if (!element.apiMappings?.[mappingType]) {
      return;
    }

    if (nextMapped[element.field] !== '') {
      return;
    }

    nextMapped[element.field] = currentValues[element.field] || '';
  });

  return nextMapped;
};

export const buildUSVerifiedAddressMapping = (validatedAddress = {}) => {
  const components = validatedAddress.components || {};
  const { formatted } = buildUSFormattedAddress(validatedAddress);
  const city = components.city_name || '';
  const region = components.state_abbreviation || '';
  const postalCode = normalizePostalCodeValue(components.zipcode || '');

  return {
    formatted,
    mapped: {
      address1: formatted.address1,
      address2: formatted.address2,
      city,
      region,
      postalCode
    },
    mapAddressLines: [
      formatted.address1,
      formatted.address2,
      [city, region, postalCode].filter(Boolean).join(' ')
    ].filter(Boolean)
  };
};
